import serverConfig, { type ServerStatusValue } from '@/config/serverConfig'
import useMinecraftStatus from '@/hooks/useMinecraftStatus'
import { useLanguage } from '@/localization'

interface ServerStatusProps {
  status: ServerStatusValue
  /** Optional caller-provided counts take precedence over the live query. */
  playerCount?: number
  maxPlayers?: number
  className?: string
}

/**
 * Status pill for a server.
 *
 * Live servers query the shared platform API. Planned servers keep the static
 * launching-soon state and never make a status request.
 */
export default function ServerStatus({
  status,
  playerCount,
  maxPlayers,
  className = '',
}: ServerStatusProps) {
  const live = serverConfig.features.liveStatusEnabled
  const liveStatus = useMinecraftStatus(live && status !== 'coming-soon')
  const { t } = useLanguage()
  const effectiveStatus = liveStatus ? (liveStatus.online ? 'online' : 'offline') : status
  const effectivePlayerCount = playerCount ?? liveStatus?.players?.online
  const effectiveMaxPlayers = maxPlayers ?? liveStatus?.players?.max

  let dot = 'bg-gold'
  let ring = 'bg-gold/15 text-gold-dark border-gold/40'
  let label = t('Launching soon')

  if (!live || effectiveStatus === 'coming-soon') {
    dot = 'bg-gold animate-pulse-slow'
    ring = 'bg-gold/15 text-gold-dark border-gold/50'
    label = t('Launching soon')
  } else if (effectiveStatus === 'online') {
    dot = 'bg-grass-dark'
    ring = 'bg-grass/15 text-grass-deep border-grass-dark/50'
    label =
      effectivePlayerCount !== undefined && effectiveMaxPlayers !== undefined
        ? `${t('Online')} · ${effectivePlayerCount}/${effectiveMaxPlayers}`
        : t('Online')
  } else {
    dot = 'bg-stone-dark'
    ring = 'bg-stone/20 text-stone-dark border-stone/50'
    label = t('Offline')
  }

  return (
    <span
      className={[
        'inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-bold tracking-wide',
        ring,
        className,
      ].join(' ')}
    >
      <span className={['w-2.5 h-2.5 rounded-full', dot].join(' ')} />
      {label}
    </span>
  )
}
